
import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Icon } from '@/components/ui/Icon'
import { CATEGORY_META, groupByCategory } from '@/lib/categories'
import { describeDevice } from '@/lib/signals'
import { btn, card, cx, iconBtn, input, label, notice, pageIntro, pageTitle, segment, tagDot, tagTone } from '@/lib/ui'
import { unitLabel, type DeviceOption, type InventoryItemDTO, type InventoryStatus } from '@/lib/types'
import { errorMessage } from '@/lib/client-api'

const STATUS_META: Record<InventoryStatus, { label: string; tone: string }> = {
  AVAILABLE: { label: 'Available', tone: tagTone.success },
  IN_USE: { label: 'In use', tone: tagTone.info },
  MAINTENANCE: { label: 'Maintenance', tone: tagTone.warning },
  RETIRED: { label: 'Retired', tone: tagTone.neutral },
}
const STATUSES = Object.keys(STATUS_META) as InventoryStatus[]

type Assignment = { projectId: string; projectName: string; label: string | null }

export function InventoryClient({
  devices,
  initialItems,
  assignments,
  apiBase,
  basePath,
}: {
  devices: DeviceOption[]
  initialItems: InventoryItemDTO[]
  assignments: Record<string, Assignment[]>
  apiBase: string
  basePath: string
}) {
  const router = useRouter()
  const [items, setItems] = useState(initialItems)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | InventoryStatus>('all')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deviceById = useMemo(() => new Map(devices.map((device) => [device.id, device])), [devices])

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const map = new Map<string, InventoryItemDTO[]>()
    for (const item of items) {
      if (statusFilter !== 'all' && item.status !== statusFilter) continue
      const device = deviceById.get(item.deviceId)
      const haystack = [
        device?.name,
        device?.manufacturer,
        device?.model,
        item.assetTag,
        item.serialNumber,
        item.location,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (needle && !haystack.includes(needle)) continue
      ;(map.get(item.deviceId) ?? map.set(item.deviceId, []).get(item.deviceId)!).push(item)
    }
    return [...map.entries()].sort(([a], [b]) => {
      const da = deviceById.get(a)
      const db = deviceById.get(b)
      return `${da?.manufacturer} ${da?.model}`.localeCompare(`${db?.manufacturer} ${db?.model}`)
    })
  }, [items, query, statusFilter, deviceById])

  const totals = useMemo(() => {
    const counts: Record<InventoryStatus, number> = { AVAILABLE: 0, IN_USE: 0, MAINTENANCE: 0, RETIRED: 0 }
    for (const item of items) counts[item.status] += 1
    return counts
  }, [items])

  async function patch(item: InventoryItemDTO, body: Partial<InventoryItemDTO>) {
    setError(null)
    const response = await fetch(`${apiBase}/inventory/${item.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      setError(payload.error ?? 'Could not update unit')
      return
    }
    setItems((current) => current.map((row) => (row.id === item.id ? (payload as InventoryItemDTO) : row)))
  }

  async function remove(item: InventoryItemDTO) {
    const device = deviceById.get(item.deviceId)
    if (!confirm(`Remove ${unitLabel(item)} (${device?.model ?? 'unit'}) from the inventory?`)) return
    setError(null)
    const response = await fetch(`${apiBase}/inventory/${item.id}`, { method: 'DELETE' })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      setError(payload.error ?? 'Could not remove unit')
      return
    }
    setItems((current) => current.filter((row) => row.id !== item.id))
    router.refresh()
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle}>Inventory</h1>
          <p className={pageIntro}>
            The physical units your company owns. Assign them to placed devices on a canvas and the
            run sheet names the exact box.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding((open) => !open)}
          className={cx(btn.base, adding ? btn.secondary : btn.primary)}
        >
          {adding ? (
            'Cancel'
          ) : (
            <>
              <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
              Add units
            </>
          )}
        </button>
      </div>

      {adding && (
        <AddUnitsForm
          devices={devices}
          apiBase={apiBase}
          onAdded={(created) => {
            setItems((current) => [...current, ...created])
            setAdding(false)
            router.refresh()
          }}
        />
      )}

      {error && <p role="alert" className={cx('mt-4', notice.error)}>{error}</p>}

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            className={cx(input, 'pl-9')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search model, asset tag, serial, location…"
            aria-label="Search inventory"
          />
        </div>
        <div className="glass flex flex-wrap items-center gap-0.5 rounded-full p-1">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            aria-pressed={statusFilter === 'all'}
            className={cx(segment.base, statusFilter === 'all' ? segment.on : segment.off)}
          >
            All
            <span className={cx('font-mono text-[10px]', statusFilter === 'all' ? 'text-on-ink/70' : 'text-muted/70')}>
              {items.length}
            </span>
          </button>
          {STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              aria-pressed={statusFilter === status}
              className={cx(segment.base, statusFilter === status ? segment.on : segment.off)}
            >
              {STATUS_META[status].label}
              <span className={cx('font-mono text-[10px]', statusFilter === status ? 'text-on-ink/70' : 'text-muted/70')}>
                {totals[status]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className={cx(card, 'gradient-hero mt-6 px-6 py-14 text-center')}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface/80 text-foreground/70 shadow-[var(--shadow-card)]">
            <Icon name="inventory" className="h-6 w-6" />
          </span>
          <p className="mx-auto mt-4 max-w-md text-sm text-muted">
            {items.length === 0
              ? 'No units yet. Add the gear you own to start tracking it.'
              : 'Nothing matches the current filter.'}
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {groups.map(([deviceId, units]) => {
            const device = deviceById.get(deviceId)
            return (
              <section key={deviceId} className={cx(card, 'overflow-hidden')}>
                <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border)] px-5 py-3.5">
                  <div>
                    <h2 className="text-sm font-semibold">
                      {device ? describeDevice(device.manufacturer, device.model) : 'Unknown device'}
                    </h2>
                    <p className="text-xs text-muted">
                      {device ? `${CATEGORY_META[device.category].singular} · ${device.name}` : ''}
                    </p>
                  </div>
                  <p className="text-[11px] text-muted">
                    {units.length} unit{units.length === 1 ? '' : 's'}
                  </p>
                </header>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
                      <tr>
                        <th className="px-5 py-2.5 font-medium">Asset tag</th>
                        <th className="px-2 py-2.5 font-medium">Serial</th>
                        <th className="px-2 py-2.5 font-medium">Status</th>
                        <th className="px-2 py-2.5 font-medium">Location</th>
                        <th className="px-2 py-2.5 font-medium">On canvas</th>
                        <th className="px-2 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((item) => (
                        <UnitRow
                          key={item.id}
                          item={item}
                          assignments={assignments[item.id] ?? []}
                          basePath={basePath}
                          onPatch={(body) => patch(item, body)}
                          onRemove={() => remove(item)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function UnitRow({
  item,
  assignments,
  basePath,
  onPatch,
  onRemove,
}: {
  item: InventoryItemDTO
  assignments: Assignment[]
  basePath: string
  onPatch: (body: Partial<InventoryItemDTO>) => void
  onRemove: () => void
}) {
  const cell =
    'w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm transition-colors hover:border-[var(--border)] focus:border-[var(--border-strong)] focus:bg-surface focus:outline-none focus:ring-2 focus:ring-[var(--ring)]'
  const commit = (field: 'assetTag' | 'serialNumber' | 'location') => (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    if ((item[field] ?? '') !== value) onPatch({ [field]: value })
  }
  return (
    <tr className="border-t border-[var(--border)]">
      <td className="px-4 py-1">
        <input className={cx(cell, 'font-mono')} defaultValue={item.assetTag ?? ''} placeholder="—" onBlur={commit('assetTag')} aria-label="Asset tag" />
      </td>
      <td className="px-1 py-1">
        <input className={cx(cell, 'font-mono')} defaultValue={item.serialNumber ?? ''} placeholder="—" onBlur={commit('serialNumber')} aria-label="Serial number" />
      </td>
      <td className="px-1 py-1">
        <span className="relative inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cx(tagDot, STATUS_META[item.status].tone)}
          />
          {/* The native chevron is dropped for the tone colour, then drawn
              back: without it the status reads as coloured text, not a
              control, and a control hidden from everyone is a control lost. */}
          <select
            className={cx(cell, 'appearance-none pr-6! font-medium', STATUS_META[item.status].tone)}
            value={item.status}
            onChange={(event) => onPatch({ status: event.target.value as InventoryStatus })}
            aria-label="Status"
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_META[status].label}
              </option>
            ))}
          </select>
          <Icon
            name="chevronDown"
            className="pointer-events-none absolute right-1.5 h-3.5 w-3.5 text-muted"
          />
        </span>
      </td>
      <td className="px-1 py-1">
        <input className={cell} defaultValue={item.location ?? ''} placeholder="—" onBlur={commit('location')} aria-label="Location" />
      </td>
      <td className="px-2 py-1 text-xs text-muted">
        {assignments.length === 0
          ? '—'
          : assignments.map((assignment, index) => (
              <span key={`${assignment.projectId}-${index}`}>
                {index > 0 && ', '}
                <Link
                  href={`${basePath}/projects/${assignment.projectId}`}
                  className="text-accent underline underline-offset-2"
                >
                  {assignment.projectName}
                </Link>
                {assignment.label ? ` (${assignment.label})` : ''}
              </span>
            ))}
      </td>
      <td className="px-3 py-1 text-right">
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${unitLabel(item)}`}
          className={cx(iconBtn, 'hover:bg-danger-soft hover:text-danger')}
        >
          <Icon name="trash" className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

function AddUnitsForm({
  devices,
  apiBase,
  onAdded,
}: {
  devices: DeviceOption[]
  apiBase: string
  onAdded: (created: InventoryItemDTO[]) => void
}) {
  // Grouped by category so a long catalogue reads as a menu, not a list.
  const grouped = useMemo(() => groupByCategory(devices), [devices])
  const [deviceId, setDeviceId] = useState(grouped[0]?.[1][0]?.id ?? '')
  const [count, setCount] = useState('1')
  const [prefix, setPrefix] = useState('')
  const [location, setLocation] = useState('')
  const [status, setStatus] = useState<InventoryStatus>('AVAILABLE')
  const [serials, setSerials] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const response = await fetch(`${apiBase}/inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deviceId,
          count: Number(count) || 1,
          assetTagPrefix: prefix,
          location,
          status,
          serialNumbers: serials
            .split(/[\n,]/)
            .map((value) => value.trim())
            .filter(Boolean),
        }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error ?? 'Could not add units')
      onAdded(payload as InventoryItemDTO[])
    } catch (cause) {
      setError(errorMessage(cause, 'Could not add units'))
    } finally {
      setBusy(false)
    }
  }

  const optional = <span className="normal-case tracking-normal text-muted/70">(optional)</span>

  return (
    <form onSubmit={submit} className={cx(card, 'mt-6 grid gap-4 p-5 sm:grid-cols-2 sm:p-6')}>
      <div className="sm:col-span-2">
        <label className={label} htmlFor="unit-device">
          Device
        </label>
        <select
          id="unit-device"
          className={cx(input, 'mt-1.5')}
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
        >
          {grouped.map(([category, items]) => (
            <optgroup key={category} label={CATEGORY_META[category].label}>
              {items.map((device) => (
                <option key={device.id} value={device.id}>
                  {describeDevice(device.manufacturer, device.model)} — {device.name}
                  {device.workspaceId ? '' : ' (catalogue)'}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <div>
        <label className={label} htmlFor="unit-count">
          How many
        </label>
        <input id="unit-count" type="number" min={1} max={100} className={cx(input, 'mt-1.5')} value={count} onChange={(event) => setCount(event.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor="unit-prefix">
          Asset tag prefix {optional}
        </label>
        <input id="unit-prefix" className={cx(input, 'mt-1.5')} value={prefix} onChange={(event) => setPrefix(event.target.value.toUpperCase())} placeholder="CAM" maxLength={20} />
        <p className="mt-1 text-[11px] text-muted">Numbers continue the sequence: CAM-01, CAM-02…</p>
      </div>
      <div>
        <label className={label} htmlFor="unit-location">
          Location {optional}
        </label>
        <input id="unit-location" className={cx(input, 'mt-1.5')} value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Flight case 3 · Studio A" />
      </div>
      <div>
        <label className={label} htmlFor="unit-status">
          Status
        </label>
        <select id="unit-status" className={cx(input, 'mt-1.5')} value={status} onChange={(event) => setStatus(event.target.value as InventoryStatus)}>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {STATUS_META[value].label}
            </option>
          ))}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={label} htmlFor="unit-serials">
          Serial numbers <span className="normal-case tracking-normal text-muted/70">(optional, one per line, in order)</span>
        </label>
        <textarea id="unit-serials" rows={2} className={cx(input, 'mt-1.5 resize-y font-mono')} value={serials} onChange={(event) => setSerials(event.target.value)} placeholder={'SN-00123\nSN-00124'} />
      </div>
      <div className="flex items-center gap-3 sm:col-span-2">
        <button type="submit" disabled={busy || !deviceId} className={cx(btn.base, btn.primary)}>
          {busy ? 'Adding…' : 'Add to inventory'}
        </button>
        {error && <p role="alert" className="text-xs text-danger">{error}</p>}
      </div>
    </form>
  )
}
